import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RiskBadge from '../components/UI/RiskBadge';
import ScanStatus from '../components/UI/ScanStatus';
import {
  Search, Plus, Download, RefreshCw,
  AlertTriangle, ShieldAlert, ShieldCheck,
  Server, Activity, X, Network, Loader2, Trash2, Clock,
  Cpu, Router, Wifi
} from 'lucide-react';
import { apiClient } from '../api';

const INPUT_CLS = 'bg-gray-950 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors';
const SELECT_CLS = 'px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyan-500 transition-colors';

const CRITICALITY_STYLES = {
  CRITICAL: 'bg-red-400/10 text-red-400 border border-red-400/20',
  HIGH:     'bg-orange-400/10 text-orange-400 border border-orange-400/20',
  MEDIUM:   'bg-amber-400/10 text-amber-400 border border-amber-400/20',
  LOW:      'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20',
};

const STATUS_MAP = {
  online: { color: 'bg-emerald-400', text: 'text-emerald-400', label: 'Online' },
  degraded: { color: 'bg-amber-400', text: 'text-amber-400', label: 'Degraded' },
  offline: { color: 'bg-red-400', text: 'text-red-400', label: 'Offline' },
};

const TYPE_ICON = {
  Firewall: Router,
  'Domain Controller': Server,
  'Web Server': Network,
  Database: Server,
  'App Server': Cpu,
  Workstation: Cpu,
  'IoT Device': Wifi,
  server: Server,
  network_device: Router,
  endpoint: Cpu,
};

const NetworkMap = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [criticalityFilter, setCriticalityFilter] = useState('all');
  const [baselineFilter, setBaselineFilter] = useState('all');
  const [riskSummary, setRiskSummary] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const [showAddModal, setShowAddModal] = useState(false);

  const [newAsset, setNewAsset] = useState({
    name: '',
    ip_address: '',
    asset_type: 'server',
    asset_criticality: 'MEDIUM'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const serversRes = await apiClient.get('/api/servers');
      const servers = serversRes.data.servers || [];
      const mappedAssets = servers.map(s => ({
        id: s.id,
        name: s.hostname,
        ip_address: s.ip_address,
        asset_type: s.os_type || 'server',
        asset_criticality: 'MEDIUM',
        status: s.last_seen ? 'online' : 'offline',
        created_at: s.created_at,
        risk_score: 0,
        is_baseline: false,
        last_scanned: s.last_seen,
      }));
      setAssets(mappedAssets);

      try {
        const riskRes = await apiClient.get('/api/dashboard/risk');
        const riskServers = riskRes.data.servers || [];
        const riskMap = new Map(riskServers.map(s => [s.id, s.risk_score]));
        setAssets(prev => prev.map(a => ({
          ...a,
          risk_score: riskMap.get(a.id) || 0,
        })));
        setRiskSummary({
          risk_distribution: {
            CRITICAL: riskServers.filter(s => s.risk_score > 75).length,
            HIGH: riskServers.filter(s => s.risk_score > 50 && s.risk_score <= 75).length,
            MEDIUM: riskServers.filter(s => s.risk_score > 25 && s.risk_score <= 50).length,
            LOW: riskServers.filter(s => s.risk_score <= 25).length,
          },
        });
      } catch (err) {
        console.error('Failed to fetch risk summary:', err);
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleManualAdd = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/register', {
        hostname: newAsset.name,
        ip_address: newAsset.ip_address,
        os_type: newAsset.asset_type,
        os_info: '',
      });
      setShowAddModal(false);
      setNewAsset({ name: '', ip_address: '', asset_type: 'server', asset_criticality: 'MEDIUM' });
      fetchData();
    } catch (err) {
      alert('Failed to add device: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteAsset = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this asset? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/api/servers/${id}`);
      setAssets(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      alert('Failed to delete asset: ' + (err.response?.data?.error || err.message));
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'IP Address', 'Type', 'Criticality', 'Risk Score', 'Baseline', 'Last Seen'];
    const rows = filteredAssets.map(a => [
      `"${a.name}"`, `"${a.ip_address}"`, `"${a.asset_type}"`, `"${a.asset_criticality}"`,
      a.risk_score, a.is_baseline ? 'Yes' : 'No',
      `"${a.last_scanned ? new Date(a.last_scanned).toLocaleString() : 'Never'}"`
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sentinel_assets_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getRiskColor = (score) => {
    if (score == null) return 'gray';
    if (score <= 25) return 'emerald';
    if (score <= 50) return 'amber';
    if (score <= 75) return 'orange';
    return 'red';
  };

  const filteredAssets = assets.filter(a => {
    const matchSearch = a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        a.ip_address?.includes(searchTerm);
    const matchRisk = riskFilter === 'all' || getRiskColor(a.risk_score) === riskFilter;
    const matchCrit = criticalityFilter === 'all' || a.asset_criticality === criticalityFilter;
    const matchBase = baselineFilter === 'all' || (baselineFilter === 'yes' ? a.is_baseline : !a.is_baseline);
    return matchSearch && matchRisk && matchCrit && matchBase;
  });

  const STAT_CARDS = [
    { label: 'Critical Risk', value: riskSummary?.risk_distribution?.CRITICAL || 0, color: 'text-red-400',     Icon: ShieldAlert,  filter: 'red'    },
    { label: 'High Risk',     value: riskSummary?.risk_distribution?.HIGH     || 0, color: 'text-orange-400', Icon: AlertTriangle, filter: 'orange' },
    { label: 'Medium Risk',   value: riskSummary?.risk_distribution?.MEDIUM   || 0, color: 'text-amber-400',  Icon: Activity,      filter: 'amber'  },
    { label: 'Low Risk',      value: riskSummary?.risk_distribution?.LOW      || 0, color: 'text-emerald-400',Icon: ShieldCheck,   filter: 'emerald'},
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Network Map</h1>
          <p className="text-sm text-gray-500 mt-1">
            Discover and scan network devices · PRD: ASSET-01 (Section 7.6)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-medium rounded ${viewMode === 'list' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('topology')}
              className={`px-3 py-1.5 text-xs font-medium rounded ${viewMode === 'topology' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
            >
              Topology
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Device
        </button>
        <button
          onClick={() => navigate('/scanner')}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors"
        >
          <Network className="w-4 h-4" />
          Scan Network
        </button>
      </div>

      <ScanStatus onScanComplete={fetchData} />

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-3">
        {STAT_CARDS.map(({ label, value, color, Icon, filter }) => {
          const active = riskFilter === filter;
          return (
            <button
              key={label}
              onClick={() => setRiskFilter(active ? 'all' : filter)}
              className={`bg-gray-900 border rounded-xl p-4 text-left transition-colors hover:border-gray-600 ${
                active ? 'border-cyan-400/50 ring-1 ring-cyan-400/20' : 'border-gray-700/50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-gray-600 mt-0.5">Devices</p>
            </button>
          );
        })}
      </div>

      {/* Main content based on view mode */}
      {viewMode === 'topology' ? (
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Network className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-semibold text-white">Network Topology</h2>
            </div>
          </div>

          <div className="relative h-80 bg-gray-950/60 rounded-lg border border-gray-800 flex items-center justify-center overflow-hidden mb-6">
            <div className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: 'linear-gradient(rgba(34,211,238,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.3) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            />
            {assets.length > 0 ? (
              <div className="grid grid-cols-4 gap-4 p-4">
                {assets.slice(0, 8).map((asset) => {
                  const status = asset.status === 'scanning' ? STATUS_MAP.degraded : STATUS_MAP.online;
                  const Icon = TYPE_ICON[asset.asset_type] || Server;
                  return (
                    <div key={asset.id} className="bg-gray-950/60 border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors cursor-pointer" onClick={() => navigate(`/assets/${asset.id}`)}>
                      <div className="flex items-start justify-between mb-2">
                        <Icon className="w-5 h-5 text-gray-500" />
                        <span className={`w-2 h-2 rounded-full ${status.color}`} />
                      </div>
                      <p className="text-sm font-medium text-white">{asset.name}</p>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{asset.ip_address}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">{asset.asset_type}</span>
                        <span className={`text-xs font-medium ${status.text}`}>{status.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="relative z-10 text-xs text-gray-600 font-mono">NETWORK TOPOLOGY — AWAITING DATA FEED</span>
            )}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/50 gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name or IP address…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-4 py-2 ${INPUT_CLS}`}
              />
            </div>
            <div className="flex gap-2">
              <select value={criticalityFilter} onChange={e => setCriticalityFilter(e.target.value)} className={SELECT_CLS}>
                <option value="all">All Criticality</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
              <select value={baselineFilter} onChange={e => setBaselineFilter(e.target.value)} className={SELECT_CLS}>
                <option value="all">All Baselines</option>
                <option value="yes">Baseline Only</option>
                <option value="no">Non-Baseline</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Loading inventory…</div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">No assets found.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700/50">
                  {['Asset Name', 'IP Address', 'Risk Level', 'Criticality', 'Baseline', 'Last Seen', 'Actions'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map(a => (
                  <tr key={a.id} className="border-b border-gray-700/30 hover:bg-gray-800/30 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        {a.status === 'scanning'
                          ? <Loader2 className="w-4 h-4 text-cyan-400 animate-spin flex-shrink-0" />
                          : <Server className="w-4 h-4 text-gray-600 flex-shrink-0" />
                        }
                        {String(a.id).startsWith('pending') ? (
                          <span className="text-sm text-cyan-400 font-medium italic">{a.name}</span>
                        ) : (
                          <button
                            onClick={() => navigate(`/assets/${a.id}`)}
                            className="text-sm font-medium text-white hover:text-cyan-400 transition-colors text-left"
                          >
                            {a.name}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <code className="text-xs bg-gray-800 border border-gray-700 px-2 py-0.5 rounded text-gray-300">
                        {a.ip_address || '—'}
                      </code>
                    </td>
                    <td className="px-5 py-3.5">
                      <RiskBadge score={a.risk_score} />
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${CRITICALITY_STYLES[a.asset_criticality] || CRITICALITY_STYLES.LOW}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {a.asset_criticality}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      {a.is_baseline ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">
                          <ShieldCheck className="w-3 h-3" /> Baseline
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {a.last_scanned ? new Date(a.last_scanned).toLocaleString() : 'Never'}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {!String(a.id).startsWith('pending') && (
                        <button
                          onClick={e => handleDeleteAsset(e, a.id)}
                          className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-400/10 rounded-md transition-colors"
                          title="Delete Asset"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add Asset Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/80 backdrop-blur-sm p-4">
          <div className="bg-gray-900 border border-gray-700/50 rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Add Manual Asset</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-300 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleManualAdd} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Asset Name</label>
                <input
                  type="text"
                  value={newAsset.name}
                  onChange={e => setNewAsset({ ...newAsset, name: e.target.value })}
                  className={`w-full px-3 py-2 ${INPUT_CLS}`}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">IP Address</label>
                  <input
                    type="text"
                    value={newAsset.ip_address}
                    onChange={e => setNewAsset({ ...newAsset, ip_address: e.target.value })}
                    className={`w-full px-3 py-2 ${INPUT_CLS}`}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Criticality</label>
                  <select
                    value={newAsset.asset_criticality}
                    onChange={e => setNewAsset({ ...newAsset, asset_criticality: e.target.value })}
                    className={`w-full px-3 py-2 ${INPUT_CLS}`}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors"
                >
                  Add Asset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkMap;